/* eslint-disable react-hooks/exhaustive-deps */
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import TranslateIcon from "@mui/icons-material/Translate";
import ContentShell from "components/ContentShell";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { SimpleSelectInput } from "components/FormSelectInput";
import { SimpleTextInput } from "components/FormTextInput";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import configData from "config/config.json";
import useStyles from "styles/pack/packEditor";
import {
  createPack2,
  getBoosters,
  getPackImages,
  getPlayerPacks,
  listPackLibraryImages,
  packInfo,
  updatePack2,
  validatePackSlots,
} from "services/packServices";
import {
  AddAndDelete,
  BoosterSlot,
  buildLabel,
  NamedPlayerSlot,
  PlayerPoolSlot,
  PlayerSlot,
} from "./Slots";
import { deepCopy, getUniquesArrayObject } from "utils/miscUtils";
import sessionHelper from "services/sessionHelper";
import FilePicker from "components/FilePicker";
import ImagePreviewBox from "components/ImagePreviewBox";
import { defaultPageNo, defaultPageSize } from "utils/constants";
import { packSchema } from "utils/validation";
import {
  uploadImageV2,
  uploadContentTranslations,
} from "services/uploadService";
import ViewLanguageModal from "screens/modals/ViewLanguageModal";
import useOpenClose from "hooks/useOpenClose";
import BulkTranslationUpload from "components/BulkTranslationUpload";
import TranslationButtons from "components/TranslationButtons";
import { getPackCategories } from "services/packCategoryServices";
import SearchableInput from "components/SearchableInput";
import { getPlayerCards } from "services/playerCardService";
import debounce from "lodash.debounce";
import { SimpleMultiSelectInput } from "components/FormMultiSelectInput";
import { InputShell } from "components/FormInputShell";
import CustomChip from "components/CustomChip";
import CustomButton from "components/CustomButton";
import { PackValidationDialog } from "components/PackValidationDialog";
import SimpleSwitch from "components/Switch";
import { DeleteOutlineRounded } from "@mui/icons-material";
import { PermittedRoles } from "utils/permissions";
import { ImageType, DEFAULT_IMAGE_DIMENSIONS } from "utils/imageConstants";

const PlayerAndBooster = ["Player", "Booster"];

const operatorPriceTemplate = {
  operator: "",
  price: 0,
};

const boosterProbability = {
  boosterId: "",
  probability: 100,
};

const dropProbability = {
  ovrMin: 1,
  ovrMax: 100,
  probability: 100,
};

const playerPool = {
  playerCard: "",
  probability: 0,
};

const slotTemplate = {
  type: "",
  dropProbabilities: [],
  boosterProbabilities: [],
  playerCard: {},
  amount: 0,
  playerPool: [],
  playerPosition: [],
  tags: [],
};

let packTemplate: any = {
  name: "",
  type: "",
  summary: "",
  isDisabled: false,
  price: 0,
  requiredVaultLevel: null,
  slots: [slotTemplate],
  isFTUPack: false,
  fileType: "",
  packPredefinedImageType: "",
  packImageId: "",
  startDate: "",
  endDate: "",
  imageUrl: "",
  nameTrans: {},
  summaryTrans: {},
  category: "",
  excludePlayers: [],
  operatorPackPrice: [],
  operators: [],
  operatorPacks: [], // array of { rowId, operatorId, packId }
};

// A stored UTC-midnight ISO string → a LOCAL Date on that same calendar day, so the DatePicker shows the
// intended date in every timezone. (Scheduled dates are stored as UTC instants: 00:00 start / 23:59 end.)
const isoToLocalDate = (iso: string): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

// A picked day (dayjs from the DatePicker) → a UTC-midnight ISO string for that calendar date. The backend
// fixes the exact time (00:00 on start, 23:59 on end); only the date matters here.
const dayjsToUtcIso = (date: any): string => {
  if (!date || typeof date.isValid !== "function" || !date.isValid()) return "";
  return new Date(
    Date.UTC(date.year(), date.month(), date.date(), 0, 0, 0, 0),
  ).toISOString();
};

const modify = (slot: any) => {
  switch (slot.type) {
    case "Player":
      return {
        tags: slot.tags,
        playerPosition: slot.playerPosition,
        dropProbabilities: slot.dropProbabilities?.map((dp) => {
          return {
            probability: Number(dp?.probability ?? 0),
            ovrMin: Number(dp?.ovrMin ?? 0),
            ovrMax: Number(dp?.ovrMax ?? 0),
          };
        }),
      };
    case "NamedPlayer":
      return {
        playerCard: slot.playerCard?.id,
      };
    case "Currency":
      return {
        amount: Number(slot.amount),
      };
    case "Booster":
      return {
        boosterProbabilities: slot.boosterProbabilities?.map((bp) => {
          return {
            probability: Number(bp?.probability ?? 0),
            boosterId: bp?.boosterId?.id ?? bp?.boosterId,
          };
        }),
      };
    case "PlayerPool":
      return {
        playerPool: slot.playerPool.map((item) => ({
          playerCard: item?.playerCard?.id,
          probability: Number(item?.probability),
        })),
      };
    default:
      return slot;
  }
};

// ─── Section Card ───────────────────────────────────────────
// Mirrors ChallengesEditor's section styling so the pack editor matches the rest
// of the CMS: a numbered, icon-led, accent-bordered Paper card per section.
const SectionCard = ({
  step,
  icon,
  title,
  subtitle,
  accent = "#1976d2",
  children,
  isComplete,
}: {
  step: number;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  accent?: string;
  children: React.ReactNode;
  isComplete?: boolean;
}) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid",
      borderColor: isComplete ? `${accent}60` : "divider",
      borderRadius: 3,
      mb: 3,
      overflow: "hidden",
      transition: "border-color 0.3s, box-shadow 0.3s",
      "&:hover": { boxShadow: `0 2px 16px ${accent}12` },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2.5,
        py: 2,
        borderBottom: "1px solid",
        borderBottomColor: "divider",
        borderLeft: `4px solid ${accent}`,
        background: `linear-gradient(135deg, ${accent}0a 0%, transparent 100%)`,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 700,
          flexShrink: 0,
          boxShadow: `0 2px 8px ${accent}40`,
        }}
      >
        {step}
      </Box>
      <Box sx={{ color: accent, display: "flex", fontSize: 20 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          color="text.primary"
          sx={{ lineHeight: 1.3 }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {isComplete !== undefined && (
        <Tooltip title={isComplete ? "Section complete" : "Not set"}>
          <Box
            sx={{ display: "flex", color: isComplete ? "#4caf50" : "#bdbdbd" }}
          >
            {isComplete ? (
              <CheckCircleIcon fontSize="small" />
            ) : (
              <RadioButtonUncheckedIcon fontSize="small" />
            )}
          </Box>
        </Tooltip>
      )}
    </Box>
    <Box sx={{ p: 3 }}>{children}</Box>
  </Paper>
);

// ─── Optional schedule date field ───────────────────────────
// The same MUI X DatePicker (with calendar icon) the Dashboard uses, but tolerant of an empty value
// (renders blank, not a red "invalid" field) and allowing future dates. `value`/`minDate` are native
// Dates on the intended local calendar day; `onChange` yields a dayjs (or null when cleared).
const ScheduleDateField = ({
  label,
  name,
  value,
  minDate,
  disabled,
  onChange,
}: {
  label: string;
  name: string;
  value: Date | null;
  minDate?: Date | null;
  disabled?: boolean;
  onChange: (date: any) => void;
}) => (
  <InputShell name={name} label={label} removeBottomMargin>
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        value={value ? dayjs(value) : null}
        format="DD/MM/YYYY"
        minDate={minDate ? dayjs(minDate) : undefined}
        disabled={disabled}
        onChange={onChange}
        slotProps={{
          textField: { size: "small", fullWidth: true },
          actionBar: { actions: ["clear", "today"] },
        }}
      />
    </LocalizationProvider>
  </InputShell>
);

const PackEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();
  const [packDetails, setPackDetails] = useState(packTemplate);
  const [loading, setLoading] = useState(false);

  const hasSchedule = !!packDetails.startDate || !!packDetails.endDate;

  // Once a pack carries dates its status belongs to the server, which enables it at the start date
  // and disables it at the end date — so the switch is only settable by hand with both dates clear.
  const isStatusScheduleControlled = hasSchedule;

  // The mirror of that: an active pack is managed by hand, so it can't also carry a schedule and
  // the dates stay locked until the status is switched off. Deliberately not applied once a
  // schedule exists — otherwise a pack the scheduler has already enabled would have both controls
  // locked and no way back.
  const isScheduleBlockedByStatus = !packDetails.isDisabled && !hasSchedule;
  const { isOpen, close, open } = useOpenClose();
  const [options, setOptions] = useState({
    slotCount: 1,
    packTypeOptions: [
      { id: "Reward", label: "Reward" },
      { id: "Store", label: "Store" },
    ],
    operatorOptions: Array.from({ length: 5 }, (v, k) => ({
      id: "id_" + (k + 1),
      label: "Operator_" + (k + 1),
    })),
    noOfSlotOptions: Array.from({ length: 30 }, (v, k) => ({
      id: k + 1,
      label: k + 1,
    })),
    slotTypeOptions: [
      { id: "Player", label: "Player" },
      { id: "NamedPlayer", label: "Named Player" },
      { id: "Currency", label: "Currency" },
      { id: "Booster", label: "Booster" },
      { id: "PlayerPool", label: "Player Pool" },
    ],
    packCategories: [],
    packCategoryOptions: [],
    images: [],
    libraryImages: [],
    selectedPackImage: null,
    playerCards: [],
    playerCardOptions: [],
    boosters: [],
    boostersOptions: [],
    rewardPacks: [],
    rewardPackOptions: [],
  });
  const [modalData, setModalData] = useState({
    language: "",
    inputValue: "",
    field: null,
    viewData: [],
  });
  const [imageDimensions, setImageDimensions] = useState<{
    targetWidth: number;
    targetHeight: number;
    originalWidth: number;
    originalHeight: number;
  } | null>(null);
  // Object-URL preview for a freshly uploaded (not-yet-saved) image, shown ONLY in the single
  // right-side preview box. Mirrored in a ref so it can be revoked on unmount.
  const [uploadedPreview, setUploadedPreview] = useState<string>("");
  const uploadedPreviewRef = useRef<string>("");
  const [playerTag, setPlayerTag] = useState({});
  const [validationDialogData, setValidationDialogData] = useState({
    open: false,
    errorMessages: [],
    validationResults: [],
  });
  const [slotValidationErrors, setSlotValidationErrors] = useState({});
  const [translationFile, setTranslationFile] = useState<File | null>(null);
  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.PLAYER.PACK.DELETE_ROLES,
  );
  const hasValidatedOnLoad = useRef(false);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (uploadedPreviewRef.current)
        URL.revokeObjectURL(uploadedPreviewRef.current);
    };
  }, []);

  const onPlayerTagChange = (e, slotIndex) => {
    const { value } = e.target;
    if (value !== ",")
      setPlayerTag((state) => ({ ...state, [slotIndex]: value }));
  };

  const getSlotTypeAndName = (slotIndex: any) => {
    let returnData = {
      type: packDetails.slots[slotIndex].type,
      name: "",
    };

    const { type } = returnData;

    if (type === "Player") returnData.name = "dropProbabilities";
    if (type === "PlayerPool") returnData.name = "playerPool";
    if (type === "Booster") returnData.name = "boosterProbabilities";

    return returnData;
  };

  const cancel = () => navigate(configData.routes.packs.packs);

  const submit = async () => {
    try {
      setLoading(true);
      let resp;

      let pack = deepCopy({
        ...packDetails,
        excludePlayers: packDetails.excludePlayers.map((player) => player.id),
      });

      delete pack?.playerDetails;

      // Exactly one image source; drop the others and any blank value that would fail scalar validation.
      if (options.selectedPackImage) {
        pack.fileType = "image/webp";
        delete pack?.packPredefinedImageType;
        delete pack?.packImageId;
      } else if (pack.packImageId) {
        delete pack?.fileType;
        delete pack?.packPredefinedImageType;
      } else if (pack.packPredefinedImageType) {
        delete pack?.fileType;
        delete pack?.packImageId;
      } else {
        delete pack?.fileType;
        delete pack?.packImageId;
        delete pack?.packPredefinedImageType;
      }

      // Optional schedule window. On create a blank date is simply omitted; on edit it has to be
      // sent as null, because an omitted key leaves the stored date in place and the window can
      // then never be cleared.
      if (!pack.startDate) {
        if (id === "create") delete pack.startDate;
        else pack.startDate = null;
      }
      if (!pack.endDate) {
        if (id === "create") delete pack.endDate;
        else pack.endDate = null;
      }

      await packSchema.validate(pack, { abortEarly: false });
      delete pack?.imageUrl;
      delete pack?.archivedAt;

      // While the schedule owns the pack's status the server decides it, so the status this form
      // shows is not sent at all. Dropped after validation, which still expects the form's own state.
      if (isStatusScheduleControlled) delete pack.isDisabled;

      let _slots = pack.slots.map((slot) => {
        let slotData = modify(slot);
        return {
          type: slot.type,
          ...slotData,
        };
      });

      const { category, ...rest } = pack;
      const operatorPackPrice = packDetails?.operatorPackPrice?.reduce(
        (acc, curr) => {
          acc[`${curr?.operator}`] = Number(curr?.price);
          return acc;
        },
        {},
      );

      if (packDetails.operatorPacks?.length) {
        for (const entry of packDetails.operatorPacks) {
          if (!entry.operatorId) {
            setLoading(false);
            showSnackbar(
              "Please select an Operator for all added Operator Packs",
              "error",
            );
            return;
          }
          if (!entry.packId) {
            setLoading(false);
            showSnackbar(
              "Reward Pack is required for all selected Operators",
              "error",
            );
            return;
          }
        }
      }

      // Convert array to the {operatorId: packId} shape the API expects
      const operatorPacksPayload = (packDetails.operatorPacks || []).reduce(
        (acc: any, entry: any) => {
          if (entry.operatorId) acc[entry.operatorId] = entry.packId;
          return acc;
        },
        {},
      );

      if (id !== "create") {
        resp = await updatePack2({
          ...rest,
          slots: _slots,
          categoryId: category,
          operatorPackPrice: operatorPackPrice,
          operatorPacks: operatorPacksPayload,
        });
      } else {
        resp = await createPack2({
          ...rest,
          slots: _slots,
          categoryId: category,
          operatorPackPrice: operatorPackPrice,
          operatorPacks: operatorPacksPayload,
        });
      }

      if (resp?.data?.fileUploadUrl) {
        await upload(resp?.data?.fileUploadUrl);
      }

      if (id === "create" && translationFile && resp?.data?._id) {
        await uploadContentTranslations(translationFile, "PACK", resp.data._id);
      }

      setOptions((st) => ({ ...st, selectedPackImage: null }));
      setSlotValidationErrors({}); // Clear validation errors on successful save

      // Check if pack has validation errors (created/updated successfully but has invalid slots)
      if (resp?.data?.isValid === false) {
        const warningMessage =
          id !== "create"
            ? "Pack updated successfully, but some slots contain errors. Fix the errors before enabling the pack."
            : "Pack created successfully, but some slots contain errors. Fix the errors before enabling the pack.";
        showSnackbar(warningMessage, "warning");
      } else {
        showSnackbar(resp?.message, "success");
      }

      navigate(configData.routes.packs.packs);
    } catch (err) {
      // Handle pack slot validation errors from backend
      const errorDetails = err?.response?.data?.details;

      if (errorDetails?.code === 4007) {
        // INVALID_PACK_SLOT_CONFIGURATION - Show inline errors in slots
        const metadata = errorDetails.metadata || {};
        const validationResults = metadata.validationResults || [];

        // Map validation results to slot errors
        const slotErrors = {};
        validationResults.forEach((result) => {
          if (!result.isValid) {
            slotErrors[result.slotIndex] = result;
          }
        });

        setSlotValidationErrors(slotErrors);
        setValidationDialogData({
          open: true,
          errorMessages: metadata.errors || [],
          validationResults: metadata.validationResults || [],
        });
        showSnackbar(
          "Pack has invalid slot configurations. Please fix the errors shown below.",
          "error",
        );
      } else if (errorDetails?.code === 4008) {
        // CANNOT_ENABLE_INVALID_PACK - Show validation dialog (blocking operation)
        const metadata = errorDetails.metadata || {};

        setValidationDialogData({
          open: true,
          errorMessages: metadata.errors || [],
          validationResults: metadata.validationResults || [],
        });
      } else if (errorDetails?.code === 4012 || errorDetails?.code === 4013) {
        // Schedule window not open — the server explains which date is blocking the change.
        showSnackbar(
          err?.response?.data?.message || "Pack schedule prevents this change.",
          "error",
        );
      } else if (err.inner) {
        // Yup validation errors
        err.inner?.forEach((error) => {
          showSnackbar(error.message, "error");
        });
      } else {
        // Generic error handling
        showSnackbar(
          err?.response?.data?.message || err.message || "An error occurred",
          "error",
        );
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const updateSlotCount = (e: any) => {
    setLoading(true);
    const _value = Number(e.target.value);
    let pack = { ...packDetails };

    if (_value > options.slotCount) {
      pack.slots = [
        ...pack.slots,
        ...Array.from(
          { length: _value - options.slotCount },
          (_, k) => slotTemplate,
        ),
      ];
    } else {
      pack.slots = pack.slots.slice(0, _value);
    }

    setOptions((st) => ({ ...st, slotCount: _value }));
    setPackDetails(pack);
    setLoading(false);
  };

  const onChange = (e: any) => {
    const { name, value } = e?.target;

    let parsedValue: any = value;
    if (name === "price") {
      parsedValue = Number(value);
    } else if (name === "requiredVaultLevel") {
      // Empty input clears the restriction (null = no vault gate).
      parsedValue = value === "" || value === null ? null : Number(value);
    } else if (name === "isDisabled") {
      parsedValue = !packDetails.isDisabled;
    }

    let data = {
      ...packDetails,
      [name]: parsedValue,
    };

    if (["name", "summary"].includes(name)) {
      data[`${name}Trans`]["en"] = value;
    }

    if (name === "type") {
      if (value === "Store") {
        // Clear operator packs when switching to Store
        data.operatorPacks = [];
        if (!data.operatorPackPrice.length) {
          data.operatorPackPrice = [operatorPriceTemplate];
        }
      } else if (value === "Reward") {
        // Clear operators, price and vault gating when switching to Reward
        data.operators = [];
        data.operatorPackPrice = [];
        data.requiredVaultLevel = null;
        data.price = 0;
      }
    }

    setPackDetails((_state) => ({ ..._state, ...data }));
  };

  const onPlayerPosAndTagChange = (e, slotIndex) => {
    const { name, value } = e.target;

    setPackDetails((state) => {
      const currentTags = state.slots[slotIndex].tags || [];
      let _tags = currentTags;

      if (name === "tags") {
        if (
          (e.key === "Enter" || e.key === ",") &&
          playerTag[slotIndex].trim()
        ) {
          if (!currentTags.includes(playerTag[slotIndex].trim()))
            _tags.push(playerTag[slotIndex].trim());

          setPlayerTag((state) => ({ ...state, [slotIndex]: "" })); // Reset playerTag
        }
      }

      return {
        ...state,
        slots: [
          ...state.slots.slice(0, slotIndex),
          {
            ...state.slots[slotIndex],
            [name]: name === "tags" ? _tags : value,
          },
          ...state.slots.slice(slotIndex + 1),
        ],
      };
    });
  };

  const handleDeleteTag = (tagToDelete, slotIndex) => {
    setPackDetails((state) => ({
      ...state,
      slots: [
        ...packDetails.slots.slice(0, slotIndex),
        {
          ...packDetails.slots[slotIndex],
          tags: packDetails.slots[slotIndex].tags.filter(
            (tag) => tag !== tagToDelete,
          ),
        },
        ...packDetails.slots.slice(slotIndex + 1),
      ],
    }));
  };

  const onCurrencyChange = (e, sIndex) => {
    const { value } = e.target;
    let pack = { ...packDetails };
    pack.slots[sIndex].amount = value;
    setPackDetails(pack);
  };

  const handleSlotType = async (e, slotIndex) => {
    const { name, value } = e.target;

    let slotDetails = {
      ...packDetails.slots[slotIndex],
      ...slotTemplate,
      [name]: value,
    };

    let updatedData = [];

    switch (value) {
      case "Player":
        updatedData = [dropProbability];
        if (packDetails.slots[slotIndex].dropProbabilities.length) {
          updatedData = packDetails.slots[slotIndex].dropProbabilities;
        }
        slotDetails = {
          ...slotDetails,
          dropProbabilities: updatedData,
          tags: [...slotDetails.tags],
          playerPosition: [...slotDetails.playerPosition],
        };
        break;
      case "PlayerPool":
        updatedData = [playerPool];
        if (packDetails.slots[slotIndex].playerPool.length) {
          updatedData = packDetails.slots[slotIndex].playerPool;
        }
        slotDetails = {
          ...slotDetails,
          playerPool: updatedData,
        };
        break;
      case "Booster":
        updatedData = [boosterProbability];
        if (packDetails.slots[slotIndex].boosterProbabilities.length) {
          updatedData = packDetails.slots[slotIndex].boosterProbabilities;
        }
        slotDetails = {
          ...slotDetails,
          boosterProbabilities: updatedData,
        };
        break;
      default:
        // Handle unexpected cases or leave empty if nothing needs to be done
        console.warn(`Unexpected value: ${value}`);
    }

    setPackDetails((st) => ({
      ...st,
      slots: [
        ...packDetails.slots.slice(0, slotIndex),
        {
          ...slotDetails,
        },
        ...packDetails.slots.slice(slotIndex + 1),
      ],
    }));
  };

  const onDropAndBoosterChange = (e, slotIndex, nestedIndex) => {
    const { name, value } = e.target;
    const { name: _nameToChange, type } = getSlotTypeAndName(slotIndex);
    if (!PlayerAndBooster.includes(type)) return;

    // Clear validation error for this slot when user makes changes
    if (slotValidationErrors[slotIndex]) {
      setSlotValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[slotIndex];
        return newErrors;
      });
    }

    setPackDetails((state) => ({
      ...state,
      slots: [
        ...packDetails.slots.slice(0, slotIndex),
        {
          ...packDetails.slots[slotIndex],
          [_nameToChange]: [
            ...packDetails.slots[slotIndex][_nameToChange]?.slice(
              0,
              nestedIndex,
            ),
            {
              ...packDetails.slots[slotIndex][_nameToChange][nestedIndex],
              [name]: value,
            },
            ...packDetails.slots[slotIndex][_nameToChange]?.slice(
              nestedIndex + 1,
            ),
          ],
        },
        ...packDetails.slots.slice(slotIndex + 1),
      ],
    }));
  };

  const onPlayerPoolAndNamedPlayerChange = (e, slotIndex, ppIndex) => {
    const { name, value } = e.target;

    const { name: _nameToChange, type } = getSlotTypeAndName(slotIndex);

    let playerPoolData = () => {
      return [
        ...packDetails.slots[slotIndex].playerPool?.slice(0, ppIndex),
        {
          ...packDetails.slots[slotIndex].playerPool[ppIndex],
          [name]: value,
        },
        ...packDetails.slots[slotIndex].playerPool?.slice(ppIndex + 1),
      ];
    };

    const _name = type === "PlayerPool" ? _nameToChange : name;
    const pack = {
      ...packDetails,
      slots: [
        ...packDetails.slots.slice(0, slotIndex),
        {
          ...packDetails.slots[slotIndex],
          [_name]: type === "PlayerPool" ? playerPoolData() : value,
        },
        ...packDetails.slots.slice(slotIndex + 1),
      ],
    };

    setPackDetails(pack);
  };

  const add = (slotIndex: any) => {
    const { name, type } = getSlotTypeAndName(slotIndex);
    let _buildpack = (value) => ({
      ...packDetails,
      slots: [
        ...packDetails.slots.slice(0, slotIndex),
        {
          ...packDetails.slots[slotIndex],
          [name]: [...packDetails.slots[slotIndex][name], value],
        },
        ...packDetails.slots.slice(slotIndex + 1),
      ],
    });

    let pack;

    if (type === "Player") pack = _buildpack(dropProbability);
    if (type === "PlayerPool") pack = _buildpack(playerPool);
    if (type === "Booster") pack = _buildpack(boosterProbability);

    setPackDetails(pack);
  };

  const removeOne = (slotIndex, nestedIndex) => {
    const { name } = getSlotTypeAndName(slotIndex);
    let pack = { ...packDetails };
    pack.slots[slotIndex][name] = pack.slots[slotIndex][name]?.filter(
      (_, _nestIdx) => _nestIdx !== nestedIndex,
    );
    setPackDetails(pack);
  };

  const removeAll = (slotIndex: any) => {
    const { name, type } = getSlotTypeAndName(slotIndex);
    let pack = { ...packDetails };
    pack.slots[slotIndex][name] =
      type === "Player" ? [dropProbability] : [boosterProbability];
    setPackDetails(pack);
  };

  // Points the single right-side preview at a freshly uploaded file (or clears it), revoking any
  // previous object URL so only one is ever live.
  const setUploadedPreviewUrl = (file: File | null) => {
    if (uploadedPreviewRef.current)
      URL.revokeObjectURL(uploadedPreviewRef.current);
    const url = file ? URL.createObjectURL(file) : "";
    uploadedPreviewRef.current = url;
    setUploadedPreview(url);
  };

  // Exactly one image source may be active — selecting one clears the other two.
  const onSelectImage = (image: any, dimensions?: any) => {
    setOptions((st) => ({ ...st, selectedPackImage: image ? image : null }));
    if (dimensions) {
      setImageDimensions(dimensions);
    }
    setPackDetails((state) => ({
      ...state,
      packPredefinedImageType: "",
      packImageId: "",
    }));
    setUploadedPreviewUrl(image || null);
  };

  const onSelectPredefinedImage = (e: any) => {
    const { value } = e?.target ?? {};
    setOptions((st) => ({ ...st, selectedPackImage: null }));
    setUploadedPreviewUrl(null);
    setPackDetails((state) => ({
      ...state,
      packPredefinedImageType: value,
      packImageId: "",
    }));
  };

  const onSelectLibraryImage = (e: any) => {
    const { value } = e?.target ?? {};
    setOptions((st) => ({ ...st, selectedPackImage: null }));
    setUploadedPreviewUrl(null);
    setPackDetails((state) => ({
      ...state,
      packImageId: value,
      packPredefinedImageType: "",
    }));
  };

  const upload = async (url) => {
    try {
      const defaultDims = DEFAULT_IMAGE_DIMENSIONS[ImageType.PACK_IMAGE];
      const resp = await uploadImageV2({
        data: options.selectedPackImage,
        uploadUrl: url,
        resize: true,
        resizeToWidth: imageDimensions?.targetWidth || defaultDims.width,
        resizeToHeight: imageDimensions?.targetHeight || defaultDims.height,
      });

      showSnackbar("Image Uploaded", "success");
      return resp;
    } catch (error) {
      showSnackbar("Error uploading image", "error");
    }
  };

  const fetchBoosters = async (filters?: any) => {
    try {
      const resp = await getBoosters();
      const boosterOptions = resp.data?.map((boost) => ({
        id: boost?._id,
        label: boost?.displayName,
      }));
      setOptions((prevState) => {
        return {
          ...prevState,
          boostersOptions: boosterOptions,
        };
      });
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  const onBoosterSearchChange = async (searchValue) => {
    if (searchValue !== "") {
      fetchBoosters({
        pageNo: 1,
        pageSize: 10,
        search: searchValue,
        skipPagination: true,
      });
    } else {
      setOptions((prev) => {
        return {
          ...prev,
          boostersOptions: prev.boosters?.map((boost) => ({
            id: boost?._id,
            label: boost?.displayName,
          })),
        };
      });
    }
  };

  const handleOpenModal = (type, field) => {
    setModalData((state) => {
      if (type === "view") {
        let data = getTranslatedData(field);
        return { ...state, viewData: data, field, modal: type };
      } else {
        return {
          ...state,
          field,
          modal: type,
          language: "",
          inputValue: "",
        };
      }
    });
    open();
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setModalData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLangEditClick = (data: any) => {
    close();
    setModalData((state) => ({
      ...state,
      language: data?.lang,
      inputValue: data?.inputValue,
      modal: "add",
    }));
    open();
  };

  const handleLangDeleteClick = (data: any) => {
    if (data.lang === "en") {
      showSnackbar(
        "Cannot delete English translation, as it's a default languange",
        "error",
      );
      return;
    }

    // remove from view data
    setModalData((state) => ({
      ...state,
      viewData: state.viewData.filter((item) => item.lang !== data.lang),
    }));

    setPackDetails((prev) => {
      prev[modalData.field] = Object.keys(prev[modalData.field]).reduce(
        (acc, key) => {
          if (key !== data.lang) {
            acc[key] = prev[modalData.field][key];
          }
          return acc;
        },
        {},
      );
      return { ...prev };
    });
  };

  const handleAddTranslation = () => {
    const { field } = modalData;
    if (!field) {
      showSnackbar("Confused which field to add the translation", "error");
      return;
    }

    setPackDetails((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [modalData.language]: modalData.inputValue,
      },
    }));

    // if english translation is available set it field
    if (modalData.language === "en" && modalData.inputValue !== "") {
      setPackDetails((_) => ({
        ..._,
        [field?.split("T")[0]]: modalData.inputValue,
      }));
    }
    setModalData((prev) => ({
      ...prev,
      inputValue: "",
      language: "",
      field: null,
    }));
    close();
  };

  const handlePackChange = (e: any) => {
    const { value } = e.target;
    // Handle SearchableInput format (array of objects with id/label)
    if (Array.isArray(value)) {
      const operatorIds = value.map((item) =>
        typeof item === "object" ? item.id : item,
      );
      setPackDetails((prev) => ({
        ...prev,
        operators: operatorIds,
      }));
    } else {
      // Handle other input formats
      setPackDetails((prev) => ({
        ...prev,
        operators: value ? [value] : [],
      }));
    }
  };

  const getTranslatedData = (field: any) => {
    const objEntries = Object.entries(packDetails[field]);
    if (!objEntries?.length) return [];
    const _translatedData = objEntries?.map(([key, value]) => ({
      lang: key,
      inputValue: value,
    }));
    if (!_translatedData) return [];
    return _translatedData;
  };

  const fetchPlayerCards = async (filters, defaultMode?: boolean) => {
    try {
      const resp = await getPlayerCards(filters);
      let newPlayerCardsOps = resp?.items?.map((player) => {
        return {
          id: player?._id,
          label: buildLabel(player),
        };
      });

      setOptions((state) => {
        return {
          ...state,
          playerCards: getUniquesArrayObject(
            [...resp?.items, ...state.playerCards],
            "_id",
          ),
          playerCardOptions: getUniquesArrayObject([
            ...newPlayerCardsOps,
            ...state.playerCardOptions,
          ]),
        };
      });
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  const onPlayerCardSearch = async (searchValue) => {
    if (searchValue !== "") {
      fetchPlayerCards({
        pageSize: defaultPageSize,
        pageNo: defaultPageNo,
        search: searchValue,
        skipPagination: true,
      });
    } else {
      fetchPlayerCards(
        { pageSize: defaultPageSize, pageNo: defaultPageNo },
        true,
      );
    }
  };

  const optimizeSearch = debounce(onPlayerCardSearch, 300);

  const onOperatorPriceChange = (e, idx) => {
    const { name, value } = e.target;
    setPackDetails((state) => {
      return {
        ...state,
        operatorPackPrice: [
          ...state.operatorPackPrice.slice(0, idx),
          {
            ...state.operatorPackPrice[idx],
            [name]: value,
          },
          ...state.operatorPackPrice.slice(idx + 1),
        ],
      };
    });
  };

  const deleteOperatorPriceConfig = (idx: any) => {
    setPackDetails((state) => {
      return {
        ...state,
        operatorPackPrice:
          idx === "all"
            ? []
            : state.operatorPackPrice.filter((_, i) => i !== idx),
      };
    });
  };

  const addOperatorPriceConfig = () => {
    setPackDetails((state) => {
      return {
        ...state,
        operatorPackPrice: [...state.operatorPackPrice, operatorPriceTemplate],
      };
    });
  };

  // Operator Pack Management Functions for Reward type packs
  // operatorPacks is an array of { rowId, operatorId, packId } so each row
  // has a stable identity independent of which operator the user picks.
  const addOperatorPack = () => {
    const rowId = `row_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    setPackDetails((state) => ({
      ...state,
      operatorPacks: [
        ...state.operatorPacks,
        { rowId, operatorId: "", packId: "" },
      ],
    }));
  };

  const removeOperatorPack = (rowId: string) => {
    setPackDetails((state: any) => ({
      ...state,
      operatorPacks: state.operatorPacks.filter((e: any) => e.rowId !== rowId),
    }));
  };

  /** Called when the user picks a different operator in a row. */
  const updateOperatorInRow = (rowId: string, newOperatorId: string) => {
    setPackDetails((state) => ({
      ...state,
      operatorPacks: state.operatorPacks.map((e: any) =>
        e.rowId === rowId ? { ...e, operatorId: newOperatorId } : e,
      ),
    }));
  };

  /** Called when the user picks a reward pack for a row. */
  const updatePackInRow = (rowId: string, newPackId: string) => {
    setPackDetails((state) => ({
      ...state,
      operatorPacks: state.operatorPacks.map((e: any) =>
        e.rowId === rowId ? { ...e, packId: newPackId } : e,
      ),
    }));
  };

  const getAvailableOperators = (currentOperatorId: string | null = null) => {
    const usedOperatorIds = packDetails.operatorPacks
      .filter((e: any) => e.operatorId) // only rows that already have a real operator
      .map((e: any) => e.operatorId);

    return (
      sessionHelper.operatorOptions?.filter(
        (op) => op.id === currentOperatorId || !usedOperatorIds.includes(op.id),
      ) || []
    );
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const promises = [
        getBoosters(),
        getPackImages(null),
        getPackCategories(),
        getPlayerCards({ pageNo: 1, pageSize: 10 }), // fetching some player card data initially
        listPackLibraryImages(),
      ];

      if (id !== "create") promises.push(packInfo(id));

      // Fetch reward packs as part of the same batch so options are ready
      // before setPackDetails is called (fixes Reward Pack showing blank on load).
      promises.push(
        getPlayerPacks({
          pageNo: 1,
          pageSize: 100,
          sortBy: "updatedAt",
          sortDir: "DESC",
        }),
      );

      const responses = await Promise.all(promises);

      let boosterRes,
        packImagesRes,
        packCategoriesRes,
        playerCardRes,
        libraryImagesRes,
        packInfoRes,
        rewardPacksRes;

      if (id !== "create") {
        [
          boosterRes,
          packImagesRes,
          packCategoriesRes,
          playerCardRes,
          libraryImagesRes,
          packInfoRes,
          rewardPacksRes,
        ] = responses;
      } else {
        [
          boosterRes,
          packImagesRes,
          packCategoriesRes,
          playerCardRes,
          libraryImagesRes,
          rewardPacksRes,
        ] = responses;
      }

      // The pack stores packImageId = the library image's _id (ObjectId), so the option value is _id.
      // Image ID and Name are the same value, so the dropdown shows just the Image ID.
      const libraryImageOptions = (libraryImagesRes?.data ?? []).map(
        (img: any) => ({
          ...img,
          id: img?._id,
          label: img?.imageId,
        }),
      );

      const boosterOptions = boosterRes.data?.map((boost) => ({
        id: boost?._id,
        label: boost?.displayName,
      }));

      const packImagesOptions = packImagesRes.data?.map((obj) => ({
        ...obj,
        id: obj?.name,
        label: obj.name,
      }));

      const packCategoryOptions = packCategoriesRes?.data?.map((el) => {
        return {
          id: el?._id,
          label: el?.name,
        };
      });

      let playerCardsOps = playerCardRes?.items?.map((player) => {
        return {
          id: player?._id,
          label: buildLabel(player),
        };
      });

      let mergedPlayerCards = [...playerCardRes?.items];
      let mergedPlayerCardsOps = [...playerCardsOps];

      if (packInfoRes && packInfoRes?.data) {
        const keys = Object.keys(packInfoRes.data.playerDetails);
        const playerDetailsCards = keys.map((key) => ({
          _id: key,
          ...packInfoRes.data.playerDetails[key],
        }));

        for (let pc of playerDetailsCards) {
          const _player = mergedPlayerCards.find((el) => el._id === pc._id);
          if (!_player) mergedPlayerCards.push(pc);
        }

        const _playerDetailsCardOptions = playerDetailsCards.map((pc) => ({
          id: pc?._id,
          label: buildLabel(pc),
        }));

        mergedPlayerCardsOps = getUniquesArrayObject(
          [...mergedPlayerCardsOps, ..._playerDetailsCardOptions],
          "id",
        );

        const slots = packInfoRes.data?.slots.map((slot, _index) => {
          if (slot?.type === "NamedPlayer") {
            let player = packInfoRes?.data?.playerDetails[slot.playerCard];
            return {
              ...slot,
              playerCard: {
                id: slot.playerCard,
                label: buildLabel(player),
              },
            };
          } else if (slot?.type === "PlayerPool") {
            return {
              ...slot,
              playerPool: slot.playerPool?.map((pc) => {
                let player = mergedPlayerCardsOps.find(
                  (el) => el.id === pc.playerCard,
                );
                return {
                  ...pc,
                  playerCard: {
                    id: pc?.playerCard,
                    label: player?.label,
                  },
                };
              }),
            };
          }
          return slot;
        });

        const nameTrans = { ...packInfoRes.data?.nameTrans };
        const summaryTrans = { ...packInfoRes.data?.summaryTrans };

        if (packInfoRes?.data?.name && !nameTrans["en"]) {
          nameTrans["en"] = packInfoRes?.data?.name;
        } else if (packInfoRes?.data) {
          packInfoRes.data.name = nameTrans["en"];
        }
        if (packInfoRes?.data?.summary && !summaryTrans["en"]) {
          summaryTrans["en"] = packInfoRes?.data?.summary;
        } else if (packInfoRes?.data) {
          packInfoRes.data.summary = summaryTrans["en"];
        }

        const excludePlayers = packInfoRes.data.excludePlayers?.map((pid) => {
          const _player =
            mergedPlayerCardsOps[
              mergedPlayerCardsOps?.findIndex((el) => el.id === pid)
            ];
          return { id: pid, label: _player?.label };
        });

        const _opPackPrices = packInfoRes?.data?.operatorPackPrice ?? {};
        const updateOperatorPrices = [];
        for (const [operator, price] of Object.entries(_opPackPrices))
          updateOperatorPrices.push({ operator, price });

        // Convert server's {operatorId: packId} map to stable array for local state
        const operatorPacks = Object.entries(
          packInfoRes?.data?.operatorPacks ?? {},
        ).map(([operatorId, packId]) => ({
          rowId: `row_${operatorId}`,
          operatorId,
          packId,
        }));

        setPackDetails((state) => {
          return {
            ...state,
            ...packInfoRes.data,
            slots: slots,
            nameTrans: nameTrans,
            summaryTrans: summaryTrans,
            excludePlayers: excludePlayers,
            category: packInfoRes?.data?.category,
            operatorPackPrice: updateOperatorPrices ?? [],
            operatorPacks: operatorPacks,
          };
        });
      }

      // Build reward pack options from the already-fetched response
      const rewardPacks =
        rewardPacksRes?.items?.filter((pack) => pack.type === "Reward") || [];
      const rewardPackOptions = rewardPacks.map((pack) => ({
        id: pack._id,
        label: pack.name,
        idFromExcel: pack.idFromExcel,
        teamNameExcel: pack.teamNameExcel,
      }));

      setOptions((state) => {
        return {
          ...state,
          slotCount: id !== "create" ? packInfoRes?.data?.slots?.length : 1,
          images: packImagesOptions,
          libraryImages: libraryImageOptions,
          boosters: boosterRes.data,
          boostersOptions: boosterOptions,
          packCategoryOptions: packCategoryOptions,
          playerCards: mergedPlayerCards,
          playerCardOptions: mergedPlayerCardsOps,
          rewardPacks: rewardPacks,
          rewardPackOptions: rewardPackOptions,
        };
      });
      if (isMounted.current) setLoading(false);
    } catch (error) {
      if (isMounted.current) setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  // --- helper inside your component ---
  const findOption = (opts = [], val) =>
    opts?.find((o) => o?.value === val || o?.id === val) ?? null;

  const extractValueFromOptionOrEvent = (optOrEvt: any) => {
    if (!optOrEvt) return "";

    if (optOrEvt?.target && optOrEvt.target.value) {
      // when SearchableInput sends { target: { value: { id, label, ... } } }
      if (typeof optOrEvt.target.value === "object") {
        return optOrEvt.target.value.id; // or .value if your options are shaped differently
      }
      return optOrEvt.target.value;
    }

    if ("value" in optOrEvt && typeof optOrEvt.value === "object") {
      return optOrEvt.value.id;
    }

    if ("id" in optOrEvt) return optOrEvt.id;

    return "";
  };

  const getRewardOptions = (options: any) => {
    let updatedRewardPackOptions = options?.rewardPackOptions?.map(
      (data, index) => {
        return {
          label:
            data.label +
            (data.idFromExcel || data.teamNameExcel
              ? ` - ${[data.idFromExcel, data.teamNameExcel]
                  .filter(Boolean)
                  .join(" - ")}`
              : ""),
          id: data.id,
          idFromExcel: data.idFromExcel,
          teamNameExcel: data.teamNameExcel,
        };
      },
    );
    return updatedRewardPackOptions ?? [];
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Validate pack slots when pack is loaded in edit mode
  useEffect(() => {
    const validateSlotsOnLoad = async () => {
      // Only validate once and only in edit mode when slots are loaded
      if (
        hasValidatedOnLoad.current ||
        id === "create" ||
        !packDetails?.slots ||
        packDetails.slots.length === 0
      ) {
        return;
      }

      // Check if there are any Player type slots
      const hasPlayerSlots = packDetails.slots.some(
        (slot) => slot.type === "Player",
      );
      if (!hasPlayerSlots) {
        return;
      }

      try {
        // Mark as validated to prevent re-runs
        hasValidatedOnLoad.current = true;

        // Convert excludePlayers from {id, label} format to array of IDs
        const excludePlayerIds =
          packDetails.excludePlayers?.map((ep) => ep.id) || [];

        let pack = deepCopy(packDetails);

        let _slots = pack.slots.map((slot) => {
          let slotData = modify(slot);
          return {
            type: slot.type,
            ...slotData,
          };
        });

        const validationResp = await validatePackSlots(
          _slots,
          excludePlayerIds,
        );

        if (validationResp?.data && !validationResp.data.isValid) {
          // Map validation results to slot errors
          const slotErrors = {};
          validationResp.data.validationResults.forEach((result) => {
            if (!result.isValid) {
              slotErrors[result.slotIndex] = result;
            }
          });

          setSlotValidationErrors(slotErrors);

          if (Object.keys(slotErrors).length > 0) {
            showSnackbar(
              `This pack has ${
                Object.keys(slotErrors).length
              } invalid slot(s). Fix the errors before enabling.`,
              "warning",
            );
          }
        }
      } catch (error) {
        console.error("Error validating pack slots on load:", error);
        // Don't show error to user, validation is informational only
      }
    };

    validateSlotsOnLoad();
  }, [packDetails?.slots, packDetails?.excludePlayers, id]);

  const renderView = (slot, index) => {
    switch (slot?.type) {
      case "Player":
        return (
          <PlayerSlot
            slotData={slot}
            slotIndex={index}
            handleChange={onDropAndBoosterChange}
            removeOne={removeOne}
            validationError={slotValidationErrors[index]}
          />
        );
      case "NamedPlayer":
        return (
          <NamedPlayerSlot
            slotData={slot}
            slotIndex={index}
            handleChange={onPlayerPoolAndNamedPlayerChange}
            playerCards={options.playerCards}
            playerCardOptions={options.playerCardOptions}
            fetchPlayerCards={fetchPlayerCards}
          />
        );
      case "Booster":
        return (
          <BoosterSlot
            slotData={slot}
            slotIndex={index}
            options={options.boostersOptions}
            removeOne={removeOne}
            handleChange={onDropAndBoosterChange}
            onSearchChange={onBoosterSearchChange}
          />
        );
      case "Currency":
        return (
          <Box className={styles.formField}>
            <SimpleTextInput
              label="Amount"
              name="amount"
              value={packDetails.slots[index]?.amount || ""}
              onChange={(e) => onCurrencyChange(e, index)}
              type="number"
            />
          </Box>
        );
      case "PlayerPool":
        return (
          <PlayerPoolSlot
            slotData={slot}
            slotIndex={index}
            handleChange={onPlayerPoolAndNamedPlayerChange}
            playerCards={options.playerCardOptions}
            removeOne={removeOne}
            fetchPlayerCards={fetchPlayerCards}
          />
        );
      default:
        return <></>;
    }
  };

  // The single (right-side) image preview, in priority order: a freshly uploaded file, then the
  // selected library image, then the selected predefined image, then the pack's saved image.
  const selectedLibraryImage = (options.libraryImages || []).find(
    (img: any) => img.id === packDetails.packImageId,
  );
  const selectedPredefinedImage = (options.images || []).find(
    (img: any) => img.id === packDetails.packPredefinedImageType,
  );
  const packImagePreviewUrl =
    (options.selectedPackImage ? uploadedPreview : "") ||
    selectedLibraryImage?.imageUrl ||
    selectedPredefinedImage?.imageUrl ||
    packDetails?.imageUrl ||
    "";

  // Scheduling is future-only: the earliest selectable date is tomorrow (a pack enables at 00:00, so
  // today has already begun). The End Date can't precede the Start Date (same day is allowed).
  const scheduleMinDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d;
  })();

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={() => navigate(-1)}
      title={`${id === "create" ? "Create" : "Edit"} Pack`}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button variant="outlined" color="inherit" onClick={cancel}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" onClick={submit}>
            Save Pack
          </Button>
        </Box>
      }
    >
      <Box sx={{ maxWidth: 980, mx: "auto", pb: 4 }}>
        {/* ── 1. Pack Information ─────────────────────────── */}
        <SectionCard
          step={1}
          icon={<Inventory2OutlinedIcon fontSize="small" />}
          title="Pack Information"
          subtitle="Name, type, category and audience"
          accent="#1565c0"
        >
          <Box className={styles.formGrid}>
            <Box className={styles.formField}>
              <SimpleTextInput
                name="name"
                label="Pack Name"
                value={packDetails.name}
                onChange={onChange}
                button={
                  <TranslationButtons
                    field={"nameTrans"}
                    onClick={handleOpenModal}
                  />
                }
              />
            </Box>
            <Box className={styles.formField}>
              <SimpleSelectInput
                label="Pack Type"
                name="type"
                value={packDetails.type}
                options={options.packTypeOptions}
                onChange={onChange}
              />
            </Box>
            {packDetails.type === "Store" && (
              <Box className={styles.formField}>
                <SimpleTextInput
                  name="price"
                  label="Pack Price"
                  value={packDetails.price ?? ""}
                  onChange={onChange}
                />
              </Box>
            )}
            {packDetails.type === "Store" && (
              <Box className={styles.formField}>
                <SimpleTextInput
                  name="requiredVaultLevel"
                  label="Required Vault Level (leave blank for no restriction)"
                  value={packDetails.requiredVaultLevel ?? ""}
                  onChange={onChange}
                  type="number"
                />
              </Box>
            )}
            <Box className={styles.formField}>
              <SimpleTextInput
                label="Pack Summary"
                name="summary"
                value={packDetails.summary}
                onChange={onChange}
                button={
                  <TranslationButtons
                    field={"summaryTrans"}
                    onClick={handleOpenModal}
                  />
                }
              />
            </Box>
            <Box className={styles.formField}>
              <SimpleSelectInput
                label="Territory"
                name="territory"
                value={packDetails?.territory || ""}
                options={sessionHelper.countryOptions}
                onChange={onChange}
              />
            </Box>
            <Box className={styles.formField}>
              <SimpleSelectInput
                label="Pack Category"
                name="category"
                value={packDetails.category ?? ""}
                onChange={onChange}
                options={options?.packCategoryOptions || []}
              />
            </Box>
            <Box className={styles.formField} style={{ alignSelf: "center" }}>
              <SimpleSwitch
                legendText={"Pack Status"}
                name="isDisabled"
                value={!packDetails.isDisabled}
                label={packDetails.isDisabled ? "Inactive" : "Active"}
                disabled={isStatusScheduleControlled}
                helperText={
                  isStatusScheduleControlled
                    ? "Set by the schedule. Clear the dates to control the status here."
                    : undefined
                }
                onChange={onChange}
              />
            </Box>
            {packDetails?.idFromExcel && (
              <Box className={styles.formField}>
                <SimpleTextInput
                  label="Id Excel"
                  name="idFromExcel"
                  value={packDetails?.idFromExcel}
                  readOnly={true}
                  readonlyvalue={packDetails?.idFromExcel}
                />
              </Box>
            )}
            <Box className={styles.formFieldWide}>
              <SearchableInput
                selected={packDetails.excludePlayers}
                isMultiSelect={true}
                label={"Exclude Player"}
                name={"excludePlayers"}
                options={options.playerCardOptions ?? []}
                onInputChange={optimizeSearch}
                onOptionSelect={onChange}
                placeholder="Select players"
                loading={loading}
              />
            </Box>
          </Box>
        </SectionCard>

        {/* ── 2. Pack Image ──────────────────────────────── */}
        <SectionCard
          step={2}
          icon={<ImageOutlinedIcon fontSize="small" />}
          title="Pack Image"
          subtitle="Pick a predefined or library image, or upload your own"
          accent="#00838f"
        >
          <Box
            sx={{
              display: "flex",
              gap: 3,
              flexWrap: "wrap",
              alignItems: "flex-start",
            }}
          >
            {/* Source controls — choose exactly one: predefined, library, or an upload */}
            <Box
              sx={{
                flex: 1,
                minWidth: 300,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Choose one image source: a predefined image, a library image, or
                upload your own.
              </Typography>
              {!options.selectedPackImage && (
                <SimpleSelectInput
                  label="Predefined Pack Image"
                  name="packPredefinedImageType"
                  options={options.images}
                  value={packDetails?.packPredefinedImageType || ""}
                  onChange={onSelectPredefinedImage}
                />
              )}
              {!options.selectedPackImage && (
                <SimpleSelectInput
                  label="Library Image"
                  name="packImageId"
                  options={options.libraryImages}
                  value={packDetails?.packImageId || ""}
                  onChange={onSelectLibraryImage}
                />
              )}
              <FilePicker
                label="Upload Custom Image"
                showPreview={false}
                name="packImage"
                onFileSelect={onSelectImage}
                imageType={ImageType.PACK_IMAGE}
                enableDimensionControl={true}
                metaData={{
                  note: `Recommended: ${
                    DEFAULT_IMAGE_DIMENSIONS[ImageType.PACK_IMAGE].width
                  } x ${
                    DEFAULT_IMAGE_DIMENSIONS[ImageType.PACK_IMAGE].height
                  } pixels. Images will be resized and converted to WebP.`,
                }}
              />
            </Box>

            {/* Live preview of the selected image */}
            <Box sx={{ width: 210 }}>
              <Typography
                variant="body2"
                className={styles.imagePreviewLabel}
                sx={{ mb: 1 }}
              >
                {options.selectedPackImage
                  ? "New upload preview"
                  : "Selected image"}
              </Typography>
              <ImagePreviewBox
                url={packImagePreviewUrl}
                height={280}
                emptyText="No image selected"
              />
            </Box>
          </Box>
        </SectionCard>

        {/* ── 3. Scheduling ──────────────────────────────── */}
        <SectionCard
          step={3}
          icon={<CalendarMonthOutlinedIcon fontSize="small" />}
          title="Scheduling"
          subtitle="Optional — auto enable/disable window (date only)"
          accent="#2e7d32"
          isComplete={!!packDetails?.startDate && !!packDetails?.endDate}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Leave blank to manage this pack manually. When set, the pack
            automatically enables at <b>00:00 (UTC)</b> on the Start Date and
            disables at <b>23:59 (UTC)</b> on the End Date. Dates must be{" "}
            <b>tomorrow or later</b> (the same day may be used for both).
          </Typography>
          {isScheduleBlockedByStatus && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              Pack Status is <b>Active</b>, so this pack is managed by hand.
              Switch <b>Pack Status</b> off to choose a schedule.
            </Alert>
          )}
          <Box className={styles.formGrid}>
            <Box className={styles.formField}>
              <ScheduleDateField
                label="Start Date"
                name="startDate"
                // Future-only — earliest selectable date is tomorrow.
                minDate={scheduleMinDate}
                disabled={isScheduleBlockedByStatus}
                value={isoToLocalDate(packDetails?.startDate)}
                onChange={(date) =>
                  setPackDetails((state) => ({
                    ...state,
                    startDate: dayjsToUtcIso(date),
                  }))
                }
              />
            </Box>
            <Box className={styles.formField}>
              <ScheduleDateField
                label="End Date"
                name="endDate"
                // The end can't precede the start (and, like the start, can't be today or earlier).
                minDate={
                  packDetails?.startDate
                    ? isoToLocalDate(packDetails.startDate)
                    : scheduleMinDate
                }
                disabled={isScheduleBlockedByStatus}
                value={isoToLocalDate(packDetails?.endDate)}
                onChange={(date) =>
                  setPackDetails((state) => ({
                    ...state,
                    endDate: dayjsToUtcIso(date),
                  }))
                }
              />
            </Box>
          </Box>
        </SectionCard>

        {/* ── 4. Store Configuration ─────────────────────── */}
        {packDetails.type === "Store" && (
          <SectionCard
            step={4}
            icon={<StorefrontOutlinedIcon fontSize="small" />}
            title="Store Configuration"
            subtitle="Per-operator pricing and availability"
            accent="#e65100"
          >
            {/* Operator Price Configuration */}
            <Box className={styles.subsectionContainer}>
              <Typography
                variant="subtitle1"
                className={styles.subsectionTitle}
              >
                Operator Price Configuration
              </Typography>
              <Box className={styles.actionButtonsContainer}>
                <CustomButton
                  onClick={addOperatorPriceConfig}
                  variant="outlined"
                  size="small"
                >
                  Add Operator Price
                </CustomButton>
                <CustomButton
                  onClick={() => deleteOperatorPriceConfig("all")}
                  startIcon={<DeleteOutlineRounded />}
                  variant="outlined"
                  color="error"
                  size="small"
                >
                  Remove All
                </CustomButton>
              </Box>
              {packDetails?.operatorPackPrice?.map((op, idx) => (
                <Box key={idx} className={styles.operatorPriceRow}>
                  <Box className={styles.formField}>
                    <SimpleSelectInput
                      label="Operator"
                      name="operator"
                      value={op?.operator ?? ""}
                      options={sessionHelper.operatorOptions}
                      onChange={(e) => onOperatorPriceChange(e, idx)}
                    />
                  </Box>
                  <Box className={styles.formField}>
                    <SimpleTextInput
                      name="price"
                      label="Pack Price"
                      value={op?.price}
                      onChange={(e) => onOperatorPriceChange(e, idx)}
                    />
                  </Box>
                  {hasDeletePermission && (
                    <Box className={styles.deleteButtonContainer}>
                      <IconButton
                        onClick={() => deleteOperatorPriceConfig(idx)}
                        size="small"
                        className={styles.deleteButton}
                      >
                        <DeleteOutlineRounded />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>

            {/* Operators Selection */}
            <Box className={styles.subsectionContainer}>
              <Box className={styles.formFieldWide}>
                <SearchableInput
                  selected={(packDetails.operators || []).map((operatorId) => {
                    const operator = sessionHelper.operatorOptions?.find(
                      (op) => op.id === operatorId,
                    );
                    return {
                      id: operatorId,
                      label: operator?.label || operatorId,
                    };
                  })}
                  isMultiSelect={true}
                  enableSelectAll={true}
                  label={"Operators (Optional)"}
                  name={"operators"}
                  options={sessionHelper.operatorOptions || []}
                  onOptionSelect={handlePackChange}
                  placeholder="Select operators (optional)"
                  loading={loading}
                />
              </Box>
            </Box>
          </SectionCard>
        )}

        {/* ── 4. Reward Configuration ────────────────────── */}
        {packDetails.type === "Reward" && (
          <SectionCard
            step={4}
            icon={<CardGiftcardOutlinedIcon fontSize="small" />}
            title="Reward Configuration"
            subtitle="Operator-specific reward packs"
            accent="#e65100"
          >
            <Box className={styles.subsectionContainer}>
              <Typography
                variant="subtitle1"
                className={styles.subsectionTitle}
              >
                Operator Packs (Optional)
              </Typography>
              {(packDetails.operatorPacks as any[]).map((entry) => (
                <Box key={entry.rowId} className={styles.operatorPackRow}>
                  <Box className={styles.formField}>
                    <SearchableInput
                      name="operator"
                      style={{ width: 300 }}
                      label="Operator"
                      selected={
                        entry.operatorId
                          ? findOption(
                              getAvailableOperators(entry.operatorId) ?? [],
                              entry.operatorId,
                            )
                          : null
                      }
                      options={
                        getAvailableOperators(entry.operatorId || null) ?? []
                      }
                      placeholder="Select operator"
                      onOptionSelect={(optOrEvt) => {
                        const newOperatorId =
                          extractValueFromOptionOrEvent(optOrEvt) || "";
                        updateOperatorInRow(entry.rowId, newOperatorId);
                      }}
                      onInputChange={optimizeSearch}
                    />
                  </Box>

                  {/* Reward Pack SearchableInput */}
                  <Box className={styles.formField}>
                    <SearchableInput
                      name="rewardPack"
                      style={{ width: 500 }}
                      label="Reward Pack"
                      selected={findOption(
                        getRewardOptions(options),
                        entry.packId,
                      )}
                      options={getRewardOptions(options)}
                      placeholder="Select reward pack"
                      onOptionSelect={(optOrEvt) => {
                        const newPackId =
                          extractValueFromOptionOrEvent(optOrEvt) || "";
                        updatePackInRow(entry.rowId, newPackId);
                      }}
                    />
                  </Box>

                  <Box className={styles.deleteButtonContainer}>
                    <IconButton
                      onClick={() => removeOperatorPack(entry.rowId)}
                      size="small"
                      className={styles.deleteButton}
                    >
                      <DeleteOutlineRounded />
                    </IconButton>
                  </Box>
                </Box>
              ))}
              <CustomButton
                variant="outlined"
                onClick={addOperatorPack}
                size="small"
              >
                Add Operator Pack
              </CustomButton>
            </Box>
          </SectionCard>
        )}
        {/* ── Slot Configuration ─────────────────────────── */}
        <SectionCard
          step={
            packDetails.type === "Store" || packDetails.type === "Reward"
              ? 5
              : 4
          }
          icon={<LayersOutlinedIcon fontSize="small" />}
          title="Slot Configuration"
          subtitle="Define what each slot can drop"
          accent="#6a1b9a"
        >
          <Box className={styles.slotHeaderContainer}>
            <Box className={styles.slotCountContainer}>
              <SimpleSelectInput
                label="Number of Slots"
                name="noOfSlots"
                options={options.noOfSlotOptions}
                value={options.slotCount}
                onChange={updateSlotCount}
              />
            </Box>
          </Box>

          <Box className={styles.slotsContainer}>
            {Array.from({ length: options.slotCount }, (slot, k) => (
              <Box key={k} className={styles.slotCard}>
                <Box className={styles.slotHeader}>
                  <Box className={styles.slotHeaderLeft}>
                    <Typography
                      variant="subtitle1"
                      className={styles.slotTitle}
                    >
                      Slot {k + 1}
                    </Typography>
                    <Box className={styles.slotTypeContainer}>
                      <SimpleSelectInput
                        label="Slot Type"
                        name="type"
                        options={options.slotTypeOptions}
                        value={packDetails.slots[k]?.type ?? ""}
                        onChange={(e) => handleSlotType(e, k)}
                      />
                    </Box>
                  </Box>
                  <Box className={styles.slotHeaderRight}>
                    <AddAndDelete
                      isHidden={
                        packDetails.slots[k]?.type !== "PlayerPool" &&
                        !PlayerAndBooster.includes(packDetails.slots[k]?.type)
                      }
                      add={() => add(k)}
                      remove={() => removeAll(k)}
                    />
                  </Box>
                </Box>

                {/* Player Position and Tags for Player type slots */}
                {packDetails.slots[k]?.type === "Player" && (
                  <Box className={styles.playerConfigSection}>
                    <Box className={styles.playerConfigRow}>
                      <Box className={styles.formField}>
                        <SimpleMultiSelectInput
                          value={packDetails.slots[k]?.playerPosition ?? []}
                          name="playerPosition"
                          label="Player Position"
                          options={sessionHelper?.positionsOptions}
                          onChange={(e) => onPlayerPosAndTagChange(e, k)}
                        />
                      </Box>
                      <Box className={styles.playerTagsSection}>
                        <Box className={styles.tagInputContainer}>
                          <InputShell
                            label="Player Tags (Press Comma or Enter to add)"
                            name="tags"
                          >
                            <TextField
                              name="tags"
                              variant="outlined"
                              size="small"
                              placeholder="Add Player Tags"
                              value={playerTag[k] || ""}
                              onChange={(e) => onPlayerTagChange(e, k)}
                              onKeyDown={(e) => onPlayerPosAndTagChange(e, k)}
                              fullWidth
                            />
                          </InputShell>
                        </Box>
                        {packDetails.slots[k]?.tags?.length > 0 && (
                          <Box className={styles.tagsDisplayContainer}>
                            {packDetails.slots[k]?.tags?.map((tag, index) => (
                              <CustomChip
                                key={index}
                                label={tag}
                                onDelete={() => handleDeleteTag(tag, k)}
                                size="small"
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Slot Content */}
                <Box className={styles.slotContent}>
                  {renderView(packDetails.slots[k], k)}
                </Box>
              </Box>
            ))}
          </Box>
        </SectionCard>

        {/* ── Translations ───────────────────────────────── */}
        <SectionCard
          step={
            packDetails.type === "Store" || packDetails.type === "Reward"
              ? 6
              : 5
          }
          icon={<TranslateIcon fontSize="small" />}
          title="Translations"
          subtitle="Upload name & summary translations in bulk"
          accent="#00838f"
        >
          <BulkTranslationUpload
            assetType="PACK"
            assetId={id !== "create" ? id : undefined}
            fields={[
              { label: "Name", fieldName: "nameTrans" },
              { label: "Summary", fieldName: "summaryTrans" },
            ]}
            {...(id === "create"
              ? { deferUpload: true, onFileChange: setTranslationFile }
              : { onSuccess: fetchData })}
          />
        </SectionCard>
      </Box>
      <ViewLanguageModal
        label="Select Language"
        isOpen={isOpen}
        close={close}
        onChange={handleChange}
        handleOnSubmit={handleAddTranslation}
        actionHandlers={{
          handleLangEditClick: handleLangEditClick,
          handleLangDeleteClick: handleLangDeleteClick,
        }}
        modalData={modalData}
        tableColumns={[
          {
            id: "lang",
            label: "Language",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "inputValue",
            label: "Translation",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "actions",
            label: "Actions",
            alignHead: "center",
            alignBody: "center",
          },
        ]}
      />

      <PackValidationDialog
        open={validationDialogData.open}
        errorMessages={validationDialogData.errorMessages}
        validationResults={validationDialogData.validationResults}
        onClose={() =>
          setValidationDialogData({
            open: false,
            errorMessages: [],
            validationResults: [],
          })
        }
      />
    </ContentShell>
  );
};

export default PackEditor;
